import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";

import { FaUser, FaEye, FaEyeSlash, FaArrowLeft } from "react-icons/fa";
import { MdLockOutline } from "react-icons/md";
import {
  AdminBadge,
  BackLink,
  Body,
  ErrorMessage,
  Footer,
  Input,
  InputGroup,
  InputIcon,
  LoginButton,
  LoginContainer,
  Relative,
  RememberSection,
  SecurityBox,
  SecurityTitle,
  CheckboxContainer,
  Checkbox,
  CheckboxLabel,
} from "./LoginPage.styles";
import Particles from "../../../components/utils/Particles";
import { selectLoginPending } from "../../../store/slices/auth/slice";
import { login } from "../../../store/slices/auth/asyncThunks";
import { useNavigate } from "react-router-dom";

// In-memory storage za čuvanje kredencijala
let savedCredentials = {
  username: "",
  password: "",
  rememberMe: false,
};

const LoginPage = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const loading = useSelector(selectLoginPending);
  const [showPassword, setShowPassword] = useState(false);
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [rememberMe, setRememberMe] = useState(false);
  const [loginError, setLoginError] = useState(null);

  // Učitaj sačuvane kredencijale pri inicijalizaciji
  useEffect(() => {
    if (savedCredentials.rememberMe) {
      setUsername(savedCredentials.username);
      setPassword(savedCredentials.password);
      setRememberMe(savedCredentials.rememberMe);
    }
  }, []);

  const handleInput = (setValue, value) => {
    setValue(value);
  };

  const handleRememberMeChange = (e) => {
    setRememberMe(e.target.checked);
  };

  const saveCredentials = () => {
    if (rememberMe) {
      savedCredentials = {
        username: username,
        password: password,
        rememberMe: true,
      };
    } else {
      // Obriši sačuvane kredencijale ako Remember Me nije označen
      savedCredentials = {
        username: "",
        password: "",
        rememberMe: false,
      };
    }
  };

  const clearCredentials = () => {
    savedCredentials = {
      username: "",
      password: "",
      rememberMe: false,
    };
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleLogin();
    }
  };

  const handleLogin = () => {
    setLoginError(null);

    dispatch(login({ username, password }))
      .unwrap()
      .then((response) => {
        // Sačuvaj ili obriši kredencijale na osnovu Remember Me
        saveCredentials();

        const userRole = response.adminUser?.role;
        if (userRole === "admin") {
          navigate("/admin-panel/kontrolna-tabla", {
            state: { fromLogin: true },
          });
        } else if (userRole === "band") {
          navigate("/bend-panel/kontrolna-tabla", {
            state: { fromLogin: true },
          });
        }
      })
      .catch((err) => {
        setLoginError("Pogrešno korisničko ime ili lozinka");
        // Obriši kredencijale ako login nije uspešan
        if (!rememberMe) {
          clearCredentials();
        }
      });
  };

  return (
    <Body>
      <Particles />
      <BackLink href="/">
        <FaArrowLeft /> Nazad u Shop
      </BackLink>

      <LoginContainer>
        <div style={{ textAlign: "center" }}>
          <AdminBadge>Admin Login</AdminBadge>
        </div>

        <SecurityBox>
          <SecurityTitle>
            <MdLockOutline /> Sigurna konekcija
          </SecurityTitle>
          <div>Vaši podaci su zaštićeni SSL enkripcijom</div>
        </SecurityBox>

        {loginError && <ErrorMessage>{loginError}</ErrorMessage>}

        <div>
          <InputGroup>
            <Relative>
              <Input
                placeholder="Unesite username"
                value={username}
                $error={loginError}
                onChange={(e) => handleInput(setUsername, e.target.value)}
                onKeyPress={handleKeyPress}
              />
              <InputIcon>
                <FaUser />
              </InputIcon>
            </Relative>
          </InputGroup>

          <InputGroup>
            <Relative>
              <Input
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => handleInput(setPassword, e.target.value)}
                placeholder="Unesite lozinku"
                $error={loginError}
                onKeyPress={handleKeyPress}
              />
              <InputIcon onClick={() => setShowPassword(!showPassword)}>
                {showPassword ? <FaEyeSlash /> : <FaEye />}
              </InputIcon>
            </Relative>
          </InputGroup>

          <RememberSection>
            <CheckboxContainer>
              <Checkbox
                type="checkbox"
                id="rememberMe"
                checked={rememberMe}
                onChange={handleRememberMeChange}
              />
              <CheckboxLabel htmlFor="rememberMe">Zapamti me</CheckboxLabel>
            </CheckboxContainer>
          </RememberSection>

          {loading ? (
            <LoginButton $disabled>
              Prijavite se
              <l-line-spinner
                size="16"
                stroke="2"
                speed="1"
                color="#ff6b35"
                style={{ position: "absolute", right: "2rem" }}
              />
            </LoginButton>
          ) : (
            <LoginButton onClick={handleLogin}>Prijavite se</LoginButton>
          )}
        </div>

        <Footer>© 2024 МРЧ. Sva prava zadržana.</Footer>
      </LoginContainer>
    </Body>
  );
};

export default LoginPage;
